import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import {
  learnersSchoolStudentPreferences,
  learnersStudentPreferences,
} from "@/routes"
import { ActionButton } from "@/components/shared/action-buttons"
import { Send } from "lucide-react"
import { PageBody } from "@/components/ui/layout"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { useEditProps } from "./page-props"
import {
  ConfirmationState,
  TransactionDialogContent,
} from "@/components/shared/transaction-dialog"
import { Preferences } from "@/components/learners/preferences/preference-page"
import type { ShortlistSheetProps } from "@/components/learners/preferences/shortlist-sheet"
import { useCallback, useState } from "react"
import { Provider } from "react-redux"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  createPageStore,
  PageReduxContext,
  selectSubjectErrors,
  usePageSelector,
} from "@/components/learners/preferences/store"
import { Subject } from "@/components/learners/preferences/store/types"
import { subjectSelectors } from "@/components/learners/preferences/store/subjects-slice"
import { Errors } from "@/components/learners/preferences/errors"
import { useNavigate, usePathParams } from "@/lib/page-rendering"

// A student's preference form: the student's own page
// (students/:student_id/preferences/edit) and, re-exported, the school
// admin's (schools/:school_id/students/:student_id/preferences/edit). The
// chrome finds each one's trail by its component name.
export default function Page() {
  const navigate = useNavigate()
  const { preferences, school_id, student, student_id, added_preference_id } =
    useEditProps()
  const schoolInPath = usePathParams().schoolId !== undefined

  const [store] = useState(
    createPageStore({
      href: schoolInPath
        ? learnersSchoolStudentPreferences({
            schoolId: school_id,
            studentId: student_id,
          }).url
        : learnersStudentPreferences({ studentId: student_id }).url,
      data: { school_id },
    }),
  )

  const addPreference = useCallback<ShortlistSheetProps["onSelect"]>(
    ({ signedIdentifier, subjectId }, teacherId) => {
      navigate({
        href: `/learners/preferences`,
        method: "post",
        data: {
          preference: {
            es_teacher_id: teacherId,
            signed_identifier: signedIdentifier,
            subject_id: subjectId,
            school_id,
            student_id,
          },
        },
        replace: true,
      })
    },
    [navigate, school_id, student_id],
  )

  const addedPreferenceId =
    added_preference_id === null ? null : Number(added_preference_id)

  return (
    <Provider store={store} context={PageReduxContext}>
      <LearnerPageChrome
        records={{ "learners/school_students/show": student.label }}
        action={<PublishDialog />}
      />
      <PageBody>
        <Preferences
          addedPreferenceId={addedPreferenceId}
          preferences={preferences!}
          store={store}
          onSelectClass={addPreference}
        />
      </PageBody>
    </Provider>
  )
}

function PublishDialog() {
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [step, setStep] = useState<"subjects" | ConfirmationState>("subjects")

  const subjects = usePageSelector(subjectSelectors.selectSubjects)
  const { school_id } = useEditProps()
  const [subjectId, setSubjectId] = useState<number | null>(null)
  const { t } = useTranslation(["preferences", "common"])

  return (
    <AlertDialog
      open={publishDialogOpen}
      onOpenChange={(open) => {
        setPublishDialogOpen(open)
        setStep("subjects")
      }}
    >
      <AlertDialogTrigger asChild>
        <ActionButton Icon={Send} variant="solid">
          {t("publish.label")}
        </ActionButton>
      </AlertDialogTrigger>
      <TransactionDialogContent
        confirmationState={step}
        onConfirmationChange={setStep}
        route="../preference_publications"
        options={{
          method: "post",
          data: {
            subject_id: subjectId,
            school_id: school_id,
          },
        }}
        pendingDescription={t("publish.pending")}
        successDescription={t("publish.success")}
        errorDescription={t("publish.error")}
        initDescription={t("publish.description")}
        closeDialog={() => {
          setPublishDialogOpen(false)
        }}
        className="h-fit max-h-148 overflow-y-auto data-[confirmationstate=subjects]:pb-0"
      >
        {step === "subjects" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("publish.title")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("publish.subjects.description")}
              </AlertDialogDescription>

              {subjects.map((subject) => (
                <PublishSubject
                  subject={subject}
                  key={subject.id}
                  onSelectSubject={(subjectId) => {
                    setSubjectId(subjectId)
                    setStep("initial")
                  }}
                />
              ))}
            </AlertDialogHeader>
            <AlertDialogFooter className="bg-background sticky bottom-0 border-t pt-2 pb-6">
              <AlertDialogCancel>
                {t("common:actions.cancel")}
              </AlertDialogCancel>
            </AlertDialogFooter>
          </>
        )}
      </TransactionDialogContent>
    </AlertDialog>
  )
}

function PublishSubject({
  subject,
  onSelectSubject,
}: {
  subject: Subject
  onSelectSubject: (subjectId: number) => void
}) {
  const errors = usePageSelector((state) =>
    selectSubjectErrors(state, { subjectId: subject.id }),
  )

  const { t } = useTranslation(["preferences"])
  return (
    <li className="flex flex-col gap-1">
      <div className="flex flex-row items-center justify-between">
        <span>{subject.name}</span>
        <Button
          disabled={errors.length > 0}
          onClick={() => onSelectSubject(subject.id)}
        >
          {t("publish.label")}
        </Button>
      </div>
      <Errors errors={errors} />
    </li>
  )
}
